import { JSX, useEffect, useState } from "react";
import {
  Route,
  Routes,
  useLocation,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { PageTemplate } from "../shared/layout/PageTemplate";
import Dashboard from "./dashboard";
import { AuthContext } from "../context/AuthContext";
import { useContext } from "react";
import { useTranslation } from "react-i18next"; // Import useTranslation
import Login from "../authentication/login/Login";
import AccountProfile from "./user/profile";
import UserList from "./user/List";
import UserForm from "./user/Form";
import LocationList from "./locations/List";
import LocationForm from "./locations/Form";
import FloorList from "./floors/List";
import FloorForm from "./floors/Form";
import FlatList from "./flats/List";
import FlatForm from "./flats/Form";
import RoomList from "./room/List";
import RoomForm from "./room/Form";
import CustomerList from "./customers/List";
import CustomerForm from "./customers/Form";
import JobList from "./jobs/List";
import JobForm from "./jobs/Form";
import TemplateIntroList from "./templateIntro/List";
import TemplateIntroForm from "./templateIntro/Form";
import TemplateAppendixList from "./templateAppendix/List";
import TemplateAppendixForm from "./templateAppendix/Form";
import SettingsForm from "./settings/Form";
import RoomNameForm from "./room-names/Form";
import RoomNameList from "./room-names/List";
import OfferConditionNameForm from "./offer-condition-names/Form";
import OfferConditionNameList from "./offer-condition-names/List";
import MaterialForm from "./materials/Form";
import MaterialList from "./materials/List";
import MaterialTypeForm from "./material-types/Form";
import MaterialTypeList from "./material-types/List";
import FloorTypeForm from "./floor-type/Form";
import FloorTypeList from "./floor-type/List";
import SkirtingBoardForm from "./skirting-board/Form";
import SkirtingBoardList from "./skirting-board/List";
import SpecialConditionForm from "./special-condition/Form";
import SpecialConditionList from "./special-condition/List";
import SupplierForm from "./suppliers/Form";
import SupplierList from "./suppliers/List";
import JobCategoryList from "./job-categories/List";
import JobCategoryForm from "./job-categories/Form";
import JobPackageList from "./job-packages/List";
import JobPackageForm from "./job-packages/Form";
import DocumentTemplateList from "./document-templates/List";
import DocumentTemplateForm from "./document-templates/Form";
import CustomerTypeList from "./customer-types/List";
import CustomerTypeForm from "./customer-types/Form";
import FlatJobList from "./flat-jobs/List";
import FlatMaterialList from "./flat-materials/List";

export function BaseRoutes() {
  const { user, loading } = useContext(AuthContext);
  const [pageTitle, setPageTitle] = useState<string>("/");
  const { pathname } = useLocation();
  const { t } = useTranslation(); // Initialize translation hook
  const navigate = useNavigate();
  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      // Redirect to login if no token is present
      navigate("/", { replace: true });
    } else if (token && pathname === "/") {
      // Redirect to dashboard if token exists but the route is "/"
      navigate("/dashboard", { replace: true });
    }
  }, [pathname, navigate]);
  useEffect(() => {
    switch (true) {
      case pathname === "/":
        return setPageTitle(t("pageTitles.login"));

      case pathname === "/dashboard":
        return setPageTitle(t("pageTitles.dashboard"));

      case pathname === "/users":
        return setPageTitle(t("pageTitles.users"));

      case pathname === "/users/create":
        return setPageTitle(t("pageTitles.createUser"));

      case pathname.startsWith("/users/edit"):
        return setPageTitle(t("pageTitles.editUser"));

      case pathname === "/locations":
        return setPageTitle(t("pageTitles.locations"));

      case pathname === "/locations/create":
        return setPageTitle(t("pageTitles.createLocations"));

      case pathname.startsWith("/locations/edit"):
        return setPageTitle(t("pageTitles.editLocations"));
      case pathname === "/floors":
        return setPageTitle(t("pageTitles.floors"));

      case pathname === "/floors/create":
        return setPageTitle(t("pageTitles.createFloors"));

      case pathname.startsWith("/floors/edit"):
        return setPageTitle(t("pageTitles.editFloors"));
      case pathname === "/flats":
        return setPageTitle(t("pageTitles.flats"));

      case pathname === "/flats/create":
        return setPageTitle(t("pageTitles.createFlats"));

      case pathname.startsWith("/flats/edit"):
        return setPageTitle(t("pageTitles.editFlats"));

      case pathname === "/rooms":
        return setPageTitle(t("pageTitles.rooms"));

      case pathname === "/rooms/create":
        return setPageTitle(t("pageTitles.createRooms"));

      case pathname.startsWith("/rooms/edit"):
        return setPageTitle(t("pageTitles.editRooms"));

      case pathname === "/customers":
        return setPageTitle(t("pageTitles.customers"));

      case pathname === "/customers/create":
        return setPageTitle(t("pageTitles.createCustomers"));

      case pathname.startsWith("/customers/edit"):
        return setPageTitle(t("pageTitles.editCustomers"));

      case pathname === "/room-names":
        return setPageTitle(t("pageTitles.roomNames"));

      case pathname === "/room-names/create":
        return setPageTitle(t("pageTitles.createRoomNames"));

      case pathname.startsWith("/room-names/edit"):
        return setPageTitle(t("pageTitles.editRoomNames"));

      case pathname === "/offer-condition-names":
        return setPageTitle(t("pageTitles.offerConditionNames"));

      case pathname === "/offer-condition-names/create":
        return setPageTitle(t("pageTitles.createOfferConditionNames"));

      case pathname.startsWith("/offer-condition-names/edit"):
        return setPageTitle(t("pageTitles.editOfferConditionNames"));

      case pathname === "/materials":
        return setPageTitle(t("pageTitles.materials"));

      case pathname === "/materials/create":
        return setPageTitle(t("pageTitles.createMaterials"));

      case pathname.startsWith("/materials/edit"):
        return setPageTitle(t("pageTitles.editMaterials"));

      case pathname === "/floor-types":
        return setPageTitle(t("pageTitles.floorTypes"));

      case pathname === "/material-types":
        return setPageTitle(t("pageTitles.materialTypes"));

      case pathname === "/material-types/create":
        return setPageTitle(t("pageTitles.createMaterialTypes"));

      case pathname.startsWith("/material-types/edit"):
        return setPageTitle(t("pageTitles.editMaterialTypes"));

      case pathname === "/floor-types/create":
        return setPageTitle(t("pageTitles.createFloorTypes"));

      case pathname.startsWith("/floor-types/edit"):
        return setPageTitle(t("pageTitles.editFloorTypes"));

      case pathname === "/skirting-boards":
        return setPageTitle(t("pageTitles.skirtingBoards"));

      case pathname === "/skirting-boards/create":
        return setPageTitle(t("pageTitles.createSkirtingBoards"));

      case pathname.startsWith("/skirting-boards/edit"):
        return setPageTitle(t("pageTitles.editSkirtingBoards"));

      case pathname === "/special-conditions":
        return setPageTitle(t("pageTitles.specialConditions"));

      case pathname === "/special-conditions/create":
        return setPageTitle(t("pageTitles.createSpecialConditions"));

      case pathname.startsWith("/special-conditions/edit"):
        return setPageTitle(t("pageTitles.editSpecialConditions"));

      case pathname === "/suppliers":
        return setPageTitle(t("pageTitles.suppliers"));

      case pathname === "/suppliers/create":
        return setPageTitle(t("pageTitles.createSupplier"));

      case pathname.startsWith("/suppliers/edit"):
        return setPageTitle(t("pageTitles.editSupplier"));

      case pathname === "/jobs":
        return setPageTitle(t("pageTitles.jobs"));

      case pathname === "/jobs/create":
        return setPageTitle(t("pageTitles.createJob"));

      case pathname.startsWith("/jobs/edit"):
        return setPageTitle(t("pageTitles.editJob"));

      case pathname === "/job-categories":
        return setPageTitle(t("pageTitles.jobCategories"));

      case pathname === "/job-categories/create":
        return setPageTitle(t("pageTitles.createJobCategory"));

      case pathname.startsWith("/job-categories/edit"):
        return setPageTitle(t("pageTitles.editJobCategory"));

      case pathname === "/job-packages":
        return setPageTitle(t("pageTitles.jobPackages"));

      case pathname === "/job-packages/create":
        return setPageTitle(t("pageTitles.createJobPackage"));

      case pathname.startsWith("/job-packages/edit"):
        return setPageTitle(t("pageTitles.editJobPackage"));

      case pathname === "/document-templates":
        return setPageTitle(t("pageTitles.documentTemplates"));

      case pathname === "/document-templates/create":
        return setPageTitle(t("pageTitles.createDocumentTemplate"));

      case pathname.startsWith("/document-templates/edit"):
        return setPageTitle(t("pageTitles.editDocumentTemplate"));

      case pathname === "/customer-types":
        return setPageTitle(t("pageTitles.customerTypes"));

      case pathname === "/customer-types/create":
        return setPageTitle(t("pageTitles.createCustomerType"));

      case pathname.startsWith("/customer-types/edit"):
        return setPageTitle(t("pageTitles.editCustomerType"));

      case pathname === "/template-intro":
        return setPageTitle(t("pageTitles.templateIntro"));

      case pathname === "/template-intro/create":
        return setPageTitle(t("pageTitles.createTemplateIntro"));

      case pathname.startsWith("/template-intro/edit"):
        return setPageTitle(t("pageTitles.editTemplateIntro"));

      case pathname === "/template-appendix":
        return setPageTitle(t("pageTitles.templateAppendix"));

      case pathname === "/template-appendix/create":
        return setPageTitle(t("pageTitles.createTemplateAppendix"));

      case pathname.startsWith("/template-appendix/edit"):
        return setPageTitle(t("pageTitles.editTemplateAppendix"));

      case pathname === "/flat-jobs":
        return setPageTitle(t("pageTitles.flatJobs"));

      case pathname === "/flat-materials":
        return setPageTitle(t("pageTitles.flatMaterials"));

      case pathname.startsWith("/settings"):
        return setPageTitle(t("pageTitles.settings"));

      case pathname === "/profile-overview":
      case pathname === "/personal-info":
      case pathname === "/change-password":
        return setPageTitle(t("pageTitles.accountProfile"));

      default:
        setPageTitle(t("pageTitles.invalid"));
    }
  }, [pathname, t]);

  const shouldHideSidebar = !localStorage.getItem("token");

  if (loading) {
    return <div>{t("loading")}</div>;
  }

  const ProtectedRoute = ({ element }: { element: JSX.Element }) => {
    const token = localStorage.getItem("token");
    return token ? element : <Navigate to="/" replace />;
  };

  const LoginRoute = () => {
    return localStorage.getItem("token") ? (
      <Navigate to="/dashboard" />
    ) : (
      <Login />
    );
  };

  return (
    <>
      {shouldHideSidebar ? (
        <Routes>
          <Route path="/" element={<LoginRoute />} />
          <Route path="*" element={<Navigate to="/" replace />} />{" "}
          {/* Catch-all route */}
        </Routes>
      ) : (
        <PageTemplate title={pageTitle}>
          <Routes>
            <Route
              path="/dashboard"
              element={<ProtectedRoute element={<Dashboard />} />}
            />
            <Route
              path="/users"
              element={<ProtectedRoute element={<UserList />} />}
            />
            <Route
              path="/users/create"
              element={<ProtectedRoute element={<UserForm />} />}
            />
            <Route
              path="/users/edit/:id"
              element={<ProtectedRoute element={<UserForm />} />}
            />
            <Route
              path="/profile-overview"
              element={<ProtectedRoute element={<AccountProfile />} />}
            />
            <Route
              path="/personal-info"
              element={<ProtectedRoute element={<AccountProfile />} />}
            />
            <Route
              path="/change-password"
              element={<ProtectedRoute element={<AccountProfile />} />}
            />
            <Route
              path="/locations"
              element={<ProtectedRoute element={<LocationList />} />}
            />
            <Route
              path="/locations/create"
              element={<ProtectedRoute element={<LocationForm />} />}
            />
            <Route
              path="/locations/edit/:id"
              element={<ProtectedRoute element={<LocationForm />} />}
            />
            <Route
              path="/floors"
              element={<ProtectedRoute element={<FloorList />} />}
            />
            <Route
              path="/floors/create"
              element={<ProtectedRoute element={<FloorForm />} />}
            />
            <Route
              path="/floors/edit/:id"
              element={<ProtectedRoute element={<FloorForm />} />}
            />
            <Route
              path="/flats"
              element={<ProtectedRoute element={<FlatList />} />}
            />
            <Route
              path="/flats/create"
              element={<ProtectedRoute element={<FlatForm />} />}
            />
            <Route
              path="/flats/edit/:id"
              element={<ProtectedRoute element={<FlatForm />} />}
            />
            <Route
              path="/rooms"
              element={<ProtectedRoute element={<RoomList />} />}
            />
            <Route
              path="/rooms/create"
              element={<ProtectedRoute element={<RoomForm />} />}
            />
            <Route
              path="/rooms/edit/:id"
              element={<ProtectedRoute element={<RoomForm />} />}
            />
            <Route
              path="/customers"
              element={<ProtectedRoute element={<CustomerList />} />}
            />
            <Route
              path="/customers/create"
              element={<ProtectedRoute element={<CustomerForm />} />}
            />
            <Route
              path="/customers/edit/:id"
              element={<ProtectedRoute element={<CustomerForm />} />}
            />
            <Route
              path="/room-names"
              element={<ProtectedRoute element={<RoomNameList />} />}
            />
            <Route
              path="/room-names/create"
              element={<ProtectedRoute element={<RoomNameForm />} />}
            />
            <Route
              path="/room-names/edit/:id"
              element={<ProtectedRoute element={<RoomNameForm />} />}
            />
            <Route
              path="/offer-condition-names"
              element={<ProtectedRoute element={<OfferConditionNameList />} />}
            />
            <Route
              path="/offer-condition-names/create"
              element={<ProtectedRoute element={<OfferConditionNameForm />} />}
            />
            <Route
              path="/offer-condition-names/edit/:id"
              element={<ProtectedRoute element={<OfferConditionNameForm />} />}
            />
            <Route
              path="/materials"
              element={<ProtectedRoute element={<MaterialList />} />}
            />
            <Route
              path="/materials/create"
              element={<ProtectedRoute element={<MaterialForm />} />}
            />
            <Route
              path="/materials/edit/:id"
              element={<ProtectedRoute element={<MaterialForm />} />}
            />
            <Route
              path="/material-types"
              element={<ProtectedRoute element={<MaterialTypeList />} />}
            />
            <Route
              path="/material-types/create"
              element={<ProtectedRoute element={<MaterialTypeForm />} />}
            />
            <Route
              path="/material-types/edit/:id"
              element={<ProtectedRoute element={<MaterialTypeForm />} />}
            />
            <Route
              path="/floor-types"
              element={<ProtectedRoute element={<FloorTypeList />} />}
            />
            <Route
              path="/floor-types/create"
              element={<ProtectedRoute element={<FloorTypeForm />} />}
            />
            <Route
              path="/floor-types/edit/:id"
              element={<ProtectedRoute element={<FloorTypeForm />} />}
            />
            <Route
              path="/skirting-boards"
              element={<ProtectedRoute element={<SkirtingBoardList />} />}
            />
            <Route
              path="/skirting-boards/create"
              element={<ProtectedRoute element={<SkirtingBoardForm />} />}
            />
            <Route
              path="/skirting-boards/edit/:id"
              element={<ProtectedRoute element={<SkirtingBoardForm />} />}
            />
            <Route
              path="/special-conditions"
              element={<ProtectedRoute element={<SpecialConditionList />} />}
            />
            <Route
              path="/special-conditions/create"
              element={<ProtectedRoute element={<SpecialConditionForm />} />}
            />
            <Route
              path="/special-conditions/edit/:id"
              element={<ProtectedRoute element={<SpecialConditionForm />} />}
            />
            <Route
              path="/suppliers"
              element={<ProtectedRoute element={<SupplierList />} />}
            />
            <Route
              path="/suppliers/create"
              element={<ProtectedRoute element={<SupplierForm />} />}
            />
            <Route
              path="/suppliers/edit/:id"
              element={<ProtectedRoute element={<SupplierForm />} />}
            />
            <Route
              path="/jobs"
              element={<ProtectedRoute element={<JobList />} />}
            />
            <Route
              path="/jobs/create"
              element={<ProtectedRoute element={<JobForm />} />}
            />
            <Route
              path="/jobs/edit/:id"
              element={<ProtectedRoute element={<JobForm />} />}
            />
            <Route
              path="/job-categories"
              element={<ProtectedRoute element={<JobCategoryList />} />}
            />
            <Route
              path="/job-categories/create"
              element={<ProtectedRoute element={<JobCategoryForm />} />}
            />
            <Route
              path="/job-categories/edit/:id"
              element={<ProtectedRoute element={<JobCategoryForm />} />}
            />
            <Route
              path="/job-packages"
              element={<ProtectedRoute element={<JobPackageList />} />}
            />
            <Route
              path="/job-packages/create"
              element={<ProtectedRoute element={<JobPackageForm />} />}
            />
            <Route
              path="/job-packages/edit/:id"
              element={<ProtectedRoute element={<JobPackageForm />} />}
            />
            <Route
              path="/document-templates"
              element={<ProtectedRoute element={<DocumentTemplateList />} />}
            />
            <Route
              path="/document-templates/create"
              element={<ProtectedRoute element={<DocumentTemplateForm />} />}
            />
            <Route
              path="/document-templates/edit/:id"
              element={<ProtectedRoute element={<DocumentTemplateForm />} />}
            />
            <Route
              path="/customer-types"
              element={<ProtectedRoute element={<CustomerTypeList />} />}
            />
            <Route
              path="/customer-types/create"
              element={<ProtectedRoute element={<CustomerTypeForm />} />}
            />
            <Route
              path="/customer-types/edit/:id"
              element={<ProtectedRoute element={<CustomerTypeForm />} />}
            />
            <Route
              path="/template-intro"
              element={<ProtectedRoute element={<TemplateIntroList />} />}
            />
            <Route
              path="/template-intro/create"
              element={<ProtectedRoute element={<TemplateIntroForm />} />}
            />
            <Route
              path="/template-intro/edit/:id"
              element={<ProtectedRoute element={<TemplateIntroForm />} />}
            />
            <Route
              path="/template-appendix"
              element={<ProtectedRoute element={<TemplateAppendixList />} />}
            />
            <Route
              path="/template-appendix/create"
              element={<ProtectedRoute element={<TemplateAppendixForm />} />}
            />
            <Route
              path="/template-appendix/edit/:id"
              element={<ProtectedRoute element={<TemplateAppendixForm />} />}
            />
            <Route
              path="/flat-jobs"
              element={<ProtectedRoute element={<FlatJobList />} />}
            />
            <Route
              path="/flat-materials"
              element={<ProtectedRoute element={<FlatMaterialList />} />}
            />
            <Route
              path="/settings"
              element={<ProtectedRoute element={<SettingsForm />} />}
            />
            <Route
              path="*"
              element={
                localStorage.getItem("token") ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />{" "}
            {/* Catch-all route */}
          </Routes>
        </PageTemplate>
      )}
    </>
  );
}
